/**
 * The contextual legend, bottom-left of the map.
 *
 * One box that says what the colours currently on screen mean, rather than a
 * fixed key. Up to three readings can be layered at once — buildings, the
 * district choropleth, the street network — and the legacy page's single static
 * legend described only the first, so the two overlays were unexplained
 * whenever they were the ones the reader had just switched on.
 *
 * Renders nothing when there is nothing to explain.
 */

import type { Metric } from "./lib/distritos";
import type { AggregateVista } from "./lib/vista";
import { VISTA_LABELS } from "./lib/vista";
import { LETTERS, LETTER_COLORS, LETTER_TEXT } from "./lib/letters";
import { TEMP_CLASSES, TEMP_COLORS, TEMP_LABELS } from "./lib/temperatura";
import { VIABILIDADES, VIAB_COLORS, VIAB_SHORT } from "./lib/tramos";
import type { BuildingPaint } from "./useMapLibre";

export interface DistritoLegend {
  metric: Metric;
  /** Class boundaries, already computed. Empty means one flat class. */
  breaks: readonly number[];
  /** The ramp in ascending-value order, as the map is painting it. */
  ramp: readonly string[];
  /** Which granularity the ramp is painted over — it names the section. */
  vista: AggregateVista;
}

interface Props {
  paint: BuildingPaint;
  /** Null when the choropleth is off or the districts have not loaded. */
  distrito: DistritoLegend | null;
  tramos: boolean;
}

/** The road classes, minus the no-data one: it explains nothing on a legend. */
const TRAMO_KEYS = VIABILIDADES.filter((v) => v !== "sin_datos");

export function MapLegend({ paint, distrito, tramos }: Props) {
  return (
    <div className="map-legend">
      {paint === "temperatura"
        ? (
          <section>
            <h2 className="legend-title">T impulsión calefacción</h2>
            {TEMP_CLASSES.map((cls) => (
              <div className="legend-row" key={cls}>
                <span className="legend-chip" style={{ background: TEMP_COLORS[cls] }} />
                {TEMP_LABELS[cls]}
              </div>
            ))}
          </section>
        )
        : (
          <section>
            <h2 className="legend-title">Letra energética</h2>
            <div className="legend-letters">
              {LETTERS.map((letter) => (
                <span
                  key={letter}
                  className="legend-letter"
                  style={{
                    background: LETTER_COLORS[letter],
                    color: LETTER_TEXT[letter],
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>
          </section>
        )}

      {distrito && (
        <section>
          <h2 className="legend-title">
            {VISTA_LABELS[distrito.vista]} · {distrito.metric.label}
          </h2>
          {/* Swatches always read left→right in ascending VALUE order, matching
              the break list underneath. For an inverted metric that puts the
              dark tone on the left, which is the point — the legend then says
              outright which end is emphasised instead of leaving it to be
              inferred. */}
          <div className="legend-ramp">
            <span className="legend-end">menos</span>
            <span className="legend-steps">
              {distrito.ramp.map((color, i) => (
                <span key={i} className="legend-step" style={{ background: color }} />
              ))}
            </span>
            <span className="legend-end">más</span>
          </div>
          {distrito.metric.lowFirst && (
            <p className="legend-note warm">Tono más intenso = renta más baja</p>
          )}
          <p className="legend-note">
            {distrito.breaks.length + 1} clases por cuantiles
            {distrito.breaks.length > 0 && (
              <> · cortes en {distrito.breaks.map(distrito.metric.format).join(" · ")}</>
            )}
          </p>
        </section>
      )}

      {tramos && (
        <section>
          <h2 className="legend-title">Tramos: tipo de red</h2>
          {TRAMO_KEYS.map((v) => (
            <div className="legend-row" key={v}>
              <span className="legend-line" style={{ background: VIAB_COLORS[v] }} />
              {VIAB_SHORT[v]}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
